(() => {
  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartUrl = shopRoot + 'cart.js';
  const addUrl = shopRoot + 'cart/add.js';
  const changeUrl = shopRoot + 'cart/change.js';
  const clearUrl = shopRoot + 'cart/clear.js';
  let cartState = null;
  let busy = false;

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return ((Number(cents) || 0) / 100).toFixed(2) + ' €';
    }
  };

  const productCache = new Map();

  async function getProductJson(productUrl) {
    const cleanUrl = String(productUrl || '').split('?')[0];
    if (!cleanUrl) return null;
    if (productCache.has(cleanUrl)) return productCache.get(cleanUrl);

    const promise = fetch(cleanUrl + '.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error('Produit introuvable');
        return response.json();
      })
      .catch(() => null);

    productCache.set(cleanUrl, promise);
    return promise;
  }

  function getVariantPicker(form) {
    const component = form.closest('product-form-component');
    const scope = component?.closest('.shopify-section, dialog, product-card, main') || document;
    return scope.querySelector('variant-picker');
  }

  function getSelectedOptionValues(picker) {
    if (!(picker instanceof Element)) return [];

    const values = [];
    const optionGroups = picker.querySelectorAll('fieldset.variant-option, .variant-option');

    for (const group of optionGroups) {
      const checked = group.querySelector('input[type="radio"]:checked');
      const select = group.querySelector('select.variant-option__select, select[name^="options["]');
      const value = checked instanceof HTMLInputElement
        ? checked.value
        : select instanceof HTMLSelectElement
          ? select.value
          : '';
      if (value) values.push(value);
    }

    if (values.length > 0) return values;

    const fallback = picker.querySelectorAll('input[type="radio"]:checked, select.variant-option__select');
    for (const control of fallback) {
      if (control instanceof HTMLInputElement && control.value) values.push(control.value);
      if (control instanceof HTMLSelectElement && control.value) values.push(control.value);
    }

    return values;
  }

  async function resolveVariant(form) {
    const component = form.closest('product-form-component');
    const productUrl = component?.dataset.productUrl || '';
    const product = await getProductJson(productUrl);
    const picker = getVariantPicker(form);
    const selectedValues = getSelectedOptionValues(picker);

    if (product && Array.isArray(product.variants) && selectedValues.length > 0) {
      const exact = product.variants.find((variant) => {
        const options = Array.isArray(variant.options) ? variant.options.map(String) : [];
        return options.length === selectedValues.length
          && options.every((value, index) => value === String(selectedValues[index]));
      });
      if (exact) {
        return { id: String(exact.id), available: exact.available !== false, product };
      }
    }

    const hidden = form.querySelector('input[name="id"]');
    const hiddenValue = hidden instanceof HTMLInputElement ? hidden.value : '';
    if (hiddenValue && /^\d+$/.test(hiddenValue)) {
      const variant = product?.variants?.find((item) => String(item.id) === hiddenValue);
      return { id: hiddenValue, available: variant ? variant.available !== false : true, product };
    }

    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    if (urlVariant && /^\d+$/.test(urlVariant)) {
      const variant = product?.variants?.find((item) => String(item.id) === urlVariant);
      return { id: urlVariant, available: variant ? variant.available !== false : true, product };
    }

    return { id: '', available: false, product };
  }

  function setAddButtonText(button, text) {
    if (!(button instanceof HTMLButtonElement)) return;
    const textNode = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
    );
    if (textNode) textNode.textContent = ' ' + text + ' ';
  }

  async function syncProductFormState(component) {
    if (!(component instanceof HTMLElement) || busy) return;

    const form = component.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
    if (!(form instanceof HTMLFormElement)) return;

    const resolved = await resolveVariant(form);
    if (!resolved.id) return;

    const hidden = form.querySelector('input[name="id"]');
    if (hidden instanceof HTMLInputElement) hidden.value = resolved.id;

    const quantity = form.querySelector('input[name="quantity"]');
    if (quantity instanceof HTMLInputElement) {
      quantity.disabled = !resolved.available;
    }

    const button = form.querySelector('[ref="addToCartButton"], button[name="add"][type="submit"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = !resolved.available;
      button.toggleAttribute('aria-disabled', !resolved.available);
      setAddButtonText(button, resolved.available ? 'Ajouter au panier' : 'Épuisé');
    }
  }

  function syncAllProductForms() {
    document.querySelectorAll('product-form-component').forEach((component) => {
      syncProductFormState(component);
    });
  }

  function ensureCartUi() {
    let wrapper = document.getElementById('peka-live-cart');
    if (wrapper) return wrapper;

    wrapper = document.createElement('div');
    wrapper.id = 'peka-live-cart';
    wrapper.className = 'peka-live-cart';
    wrapper.innerHTML = `
      <div class="peka-live-cart__backdrop" data-peka-cart-close></div>
      <aside class="peka-live-cart__panel" role="dialog" aria-modal="true" aria-labelledby="peka-live-cart-title">
        <div class="peka-live-cart__header">
          <div>
            <p class="peka-live-cart__eyebrow">PÉKA Gourmet</p>
            <h2 id="peka-live-cart-title">Votre panier</h2>
          </div>
          <button type="button" class="peka-live-cart__close" data-peka-cart-close aria-label="Fermer">×</button>
        </div>
        <div class="peka-live-cart__message" data-peka-cart-message hidden></div>
        <div class="peka-live-cart__body" data-peka-cart-body></div>
        <div class="peka-live-cart__footer" data-peka-cart-footer></div>
      </aside>
    `;
    document.body.appendChild(wrapper);

    wrapper.addEventListener('click', async (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-peka-cart-close]')) {
        closeCart();
        return;
      }

      const quantityButton = target.closest('[data-peka-cart-qty-action]');
      if (quantityButton) {
        event.preventDefault();
        if (busy) return;

        const key = decodeURIComponent(quantityButton.getAttribute('data-peka-cart-key') || '');
        const action = quantityButton.getAttribute('data-peka-cart-qty-action');
        const currentQuantity = Number(quantityButton.getAttribute('data-peka-cart-current') || '0');
        if (!key || !['increase', 'decrease'].includes(action)) return;

        const nextQuantity = action === 'increase'
          ? currentQuantity + 1
          : Math.max(0, currentQuantity - 1);

        busy = true;
        wrapper.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(changeUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {'Content-Type':'application/json','Accept':'application/json'},
            body: JSON.stringify({id:key, quantity:nextQuantity})
          });

          const payload = await response.json().catch(() => ({}));

          if (!response.ok) {
            const message = payload.description || payload.message || 'Impossible de modifier la quantité.';
            const cart = await fetchCart().catch(() => cartState);
            renderCart(cart || {items:[], item_count:0, total_price:0, currency:'EUR'}, message);
            return;
          }

          cartState = payload;
          updateBadge(cartState);
          renderCart(cartState);
        } catch (error) {
          const cart = await fetchCart().catch(() => cartState);
          renderCart(
            cart || {items:[], item_count:0, total_price:0, currency:'EUR'},
            error instanceof Error ? error.message : 'Impossible de modifier la quantité.'
          );
        } finally {
          busy = false;
          wrapper.removeAttribute('aria-busy');
        }
        return;
      }

      const remove = target.closest('[data-peka-cart-remove]');
      if (remove) {
        event.preventDefault();
        const key = decodeURIComponent(remove.getAttribute('data-peka-cart-remove') || '');
        if (!key || busy) return;
        busy = true;
        try {
          await fetch(changeUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {'Content-Type':'application/json','Accept':'application/json'},
            body: JSON.stringify({id:key, quantity:0})
          });
          const cart = await fetchCart();
          if (document.querySelector('main[data-template="product"]')) {
            window.location.reload();
            return;
          }
          renderCart(cart);
        } finally {
          busy = false;
        }
        return;
      }

      if (target.closest('[data-peka-cart-clear]')) {
        event.preventDefault();
        if (busy) return;
        busy = true;
        try {
          await fetch(clearUrl, {
            method:'POST',
            credentials:'same-origin',
            headers:{'Accept':'application/json'}
          });
          const cart = await fetchCart();
          if (document.querySelector('main[data-template="product"]')) {
            window.location.reload();
            return;
          }
          renderCart(cart);
        } finally {
          busy = false;
        }
      }
    });

    return wrapper;
  }

  function updateBadge(cart) {
    const count = Number(cart?.item_count || 0);
    document.querySelectorAll('cart-icon').forEach((icon) => {
      const bubble = icon.querySelector('.cart-bubble');
      const countEl = icon.querySelector('.cart-bubble__text-count');
      if (!bubble || !countEl) return;

      if (count > 0) {
        bubble.classList.remove('visually-hidden');
        countEl.classList.remove('hidden');
        countEl.textContent = count > 99 ? '99+' : String(count);
        icon.classList.add('header-actions__cart-icon--has-cart');
      } else {
        bubble.classList.add('visually-hidden');
        countEl.classList.add('hidden');
        countEl.textContent = '';
        icon.classList.remove('header-actions__cart-icon--has-cart');
      }
    });
  }

  async function fetchCart() {
    const response = await fetch(cartUrl, {
      credentials:'same-origin',
      cache:'no-store',
      headers:{'Accept':'application/json'}
    });
    if (!response.ok) throw new Error('Impossible de charger le panier.');
    cartState = await response.json();
    updateBadge(cartState);
    return cartState;
  }

  function renderCart(cart, message='') {
    const wrapper = ensureCartUi();
    const body = wrapper.querySelector('[data-peka-cart-body]');
    const footer = wrapper.querySelector('[data-peka-cart-footer]');
    const messageEl = wrapper.querySelector('[data-peka-cart-message]');
    const items = Array.isArray(cart?.items) ? cart.items : [];

    if (message) {
      messageEl.hidden = false;
      messageEl.textContent = message;
    } else {
      messageEl.hidden = true;
      messageEl.textContent = '';
    }

    if (!items.length) {
      body.innerHTML = '<div class="peka-live-cart__empty"><strong>Votre panier est vide.</strong><span>Ajoutez une gourmandise PÉKA pour la retrouver ici.</span></div>';
      footer.innerHTML = '<a class="peka-live-cart__primary" href="/collections/all">Découvrir nos gourmandises</a>';
      return;
    }

    body.innerHTML = items.map((item) => {
      const image = item.image || item.featured_image?.url || '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? '<span class="peka-live-cart__variant">' + escapeHtml(item.variant_title) + '</span>'
        : '';
      const imageHtml = image
        ? '<img src="' + escapeHtml(image) + '" alt="" loading="lazy">'
        : '<div class="peka-live-cart__image-placeholder">PÉKA</div>';

      return `
        <article class="peka-live-cart__item">
          <div class="peka-live-cart__image">${imageHtml}</div>
          <div class="peka-live-cart__item-copy">
            <strong>${escapeHtml(item.product_title || item.title || '')}</strong>
            ${variant}
            <div class="peka-live-cart__qty-control" role="group" aria-label="Quantité">
              <button
                type="button"
                class="peka-live-cart__qty-button"
                data-peka-cart-qty-action="decrease"
                data-peka-cart-key="${encodeURIComponent(item.key || '')}"
                data-peka-cart-current="${Number(item.quantity || 0)}"
                aria-label="Diminuer la quantité"
              >−</button>
              <span class="peka-live-cart__qty-value" aria-live="polite">${Number(item.quantity || 0)}</span>
              <button
                type="button"
                class="peka-live-cart__qty-button"
                data-peka-cart-qty-action="increase"
                data-peka-cart-key="${encodeURIComponent(item.key || '')}"
                data-peka-cart-current="${Number(item.quantity || 0)}"
                aria-label="Augmenter la quantité"
              >+</button>
            </div>
            <span class="peka-live-cart__price">${money(item.final_line_price ?? item.line_price ?? 0, cart.currency)}</span>
          </div>
          <button type="button" class="peka-live-cart__remove" data-peka-cart-remove="${encodeURIComponent(item.key || '')}">Retirer</button>
        </article>
      `;
    }).join('');

    footer.innerHTML = `
      <div class="peka-live-cart__total">
        <span>Total</span>
        <strong>${money(cart.total_price || 0, cart.currency)}</strong>
      </div>
      <div class="peka-live-cart__actions">
        <a class="peka-live-cart__secondary" href="${shopRoot}cart">Voir le panier complet</a>
        <a class="peka-live-cart__primary" href="${shopRoot}checkout">Commander</a>
      </div>
      <button type="button" class="peka-live-cart__clear" data-peka-cart-clear>Vider le panier</button>
    `;
  }

  async function openCart(message='') {
    const wrapper = ensureCartUi();
    wrapper.classList.add('is-open');
    document.documentElement.classList.add('peka-cart-open');
    try {
      const cart = await fetchCart();
      renderCart(cart, message);
    } catch (error) {
      renderCart(cartState || {items:[],item_count:0,total_price:0,currency:'EUR'}, error instanceof Error ? error.message : 'Impossible de charger le panier.');
    }
  }

  function closeCart() {
    document.getElementById('peka-live-cart')?.classList.remove('is-open');
    document.documentElement.classList.remove('peka-cart-open');
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest('cart-icon')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openCart();
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) return;

    const isCartAdd = form.dataset.type === 'add-to-cart-form' || (form.action && form.action.includes('/cart/add'));
    if (!isCartAdd) return;

    const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
    const isRegularAdd = submitter
      ? submitter.matches('button[name="add"][type="submit"], [ref="addToCartButton"]')
      : true;

    // Leave Shopify's accelerated checkout / Shop Pay flow untouched.
    if (!isRegularAdd) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const nativeError = form.closest('product-form-component')?.querySelector('[ref="addToCartTextError"]');
    if (nativeError instanceof HTMLElement) {
      nativeError.classList.add('hidden');
      nativeError.removeAttribute('aria-live');
      const textNodes = Array.from(nativeError.childNodes).filter((node) => node.nodeType === Node.TEXT_NODE);
      for (const node of textNodes) node.textContent = '';
    }

    if (busy) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const formData = new FormData(form);
    formData.delete('sections');
    formData.delete('sections_url');

    const resolvedVariant = await resolveVariant(form);
    if (!resolvedVariant.id) {
      openCart('Sélectionnez un format avant d’ajouter ce produit.');
      return;
    }
    if (!resolvedVariant.available) {
      openCart('Cette variante est actuellement épuisée.');
      return;
    }
    formData.set('id', resolvedVariant.id);
    if (!formData.get('quantity')) formData.set('quantity', '1');

    const addButton = submitter instanceof HTMLButtonElement
      ? submitter
      : form.querySelector('button[name="add"][type="submit"], [ref="addToCartButton"]');
    const wasDisabled = addButton?.disabled || false;

    busy = true;
    if (addButton instanceof HTMLButtonElement) addButton.disabled = true;

    try {
      const response = await fetch(addUrl, {
        method:'POST',
        body:formData,
        credentials:'same-origin',
        headers:{'Accept':'application/json','X-Requested-With':'XMLHttpRequest'}
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = payload.description || payload.message || 'Cet article n’a pas pu être ajouté.';
        await openCart(message);
        return;
      }

      await openCart();
    } catch (error) {
      await openCart(error instanceof Error ? error.message : 'Cet article n’a pas pu être ajouté.');
    } finally {
      busy = false;
      if (addButton instanceof HTMLButtonElement) addButton.disabled = wasDisabled;
    }
  }, true);

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('variant-picker')) return;

    const scope = event.target.closest('.shopify-section, dialog, product-card, main');
    const nativeError = scope?.querySelector('product-form-component [ref="addToCartTextError"]');
    if (nativeError instanceof HTMLElement) {
      nativeError.classList.add('hidden');
      nativeError.removeAttribute('aria-live');
      const textNodes = Array.from(nativeError.childNodes).filter((node) => node.nodeType === Node.TEXT_NODE);
      for (const node of textNodes) node.textContent = '';
    }

    setTimeout(syncAllProductForms, 80);
    setTimeout(syncAllProductForms, 350);
  }, true);

  const productFormObserver = new MutationObserver(() => {
    if (!busy) setTimeout(syncAllProductForms, 0);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      ensureCartUi();
      fetchCart().catch(() => {});
      syncAllProductForms();
      productFormObserver.observe(document.body, {subtree:true, childList:true});
    }, {once:true});
  } else {
    ensureCartUi();
    fetchCart().catch(() => {});
    syncAllProductForms();
    productFormObserver.observe(document.body, {subtree:true, childList:true});
  }
})();